'use client';

import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { InboxNotiType } from '@diritto/common/notification/constants';
import { InboxNotiDto } from '@diritto/common/notification/dtos/inbox-noti.dto';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { accurateItems, flatItems } from '@diritto/common/utils/array';
import { InboxNotiList } from 'app-components/inbox/InboxNotiList';
import { InboxTab } from 'app-components/inbox/items/InboxTab';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useGetInboxNotificationsInfiniteQuery } from 'queries/noti/useGetInboxNotificationsInfiniteQuery';
import { useReadAllInboxNotificationsMutation } from 'queries/noti/useReadAllInboxNotificationsMutation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BlockAuth } from 'ui-components/common/BlockAuth';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { FullPageLoading } from 'ui-components/common/Loading';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { BackButton, HomeButton } from 'ui-components/navigations/HeaderButtons';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { ContentBodyContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';

export const InboxPage = ({ isApp }: { isApp?: boolean }) => {
  useInAppHeader({ screen: WebViewScreen.Inbox, hideHeader: true });
  useInAppStatusBar(WebViewScreen.Inbox);

  const { isLoggedIn } = useAuthState();
  const { tracker } = useTrackingState();
  const [selectedInboxNotiType, setSelectedInboxNotiType] = useState(InboxNotiType.All);

  useEffect(() => {
    tracker.track(TrackingEventType.ViewInbox);
  }, [tracker]);

  const { data, hasNextPage, fetchNextPage, isLoading } = useGetInboxNotificationsInfiniteQuery(selectedInboxNotiType);
  const { mutate: mutateReadAll } = useReadAllInboxNotificationsMutation();

  const handleChangeInboxNotiType = useCallback(
    (inboxNotiType: InboxNotiType) => setSelectedInboxNotiType(inboxNotiType),
    [],
  );

  const { inboxNotifications, contents, episodes } = useMemo(
    () => ({
      inboxNotifications: flatItems<InboxNotiDto>('inboxNotifications', data?.pages),
      contents: accurateItems<ContentPublicRelayDto>('contents', data?.pages),
      episodes: accurateItems<EpisodePublicRelayDto>('episodes', data?.pages),
    }),
    [data],
  );

  const leftButtons = useMemo(() => [<BackButton key="back" />, <HomeButton key="home" />], []);

  useEffect(() => {
    mutateReadAll();
  }, [mutateReadAll]);

  return (
    <>
      {isLoading && !inboxNotifications ? <FullPageLoading /> : null}
      <MutableHeaderLayout title="알림" showHeaderInApp leftButtons={leftButtons} isApp={isApp}>
        <InboxTab selectedInboxNotiType={selectedInboxNotiType} onChange={handleChangeInboxNotiType} />
        <ContentBodyContainer>
          {isLoggedIn ? (
            <InfiniteScrollWrapper loadMore={fetchNextPage} hasNext={hasNextPage}>
              {inboxNotifications && (
                <InboxNotiList inboxNotifications={inboxNotifications} contents={contents} episodes={episodes} />
              )}
            </InfiniteScrollWrapper>
          ) : (
            <BlockAuth />
          )}
        </ContentBodyContainer>
      </MutableHeaderLayout>
    </>
  );
};
